import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { X, ChevronLeft, ChevronRight, Check, ChevronDown, ChevronUp, FileText, HelpCircle } from 'lucide-react';
import { saveAssessmentResponse, savePersonalAssessmentResponse } from '../services/api';
import { BUSINESS_CATEGORY_DESCRIPTIONS, PERSONAL_CATEGORY_DESCRIPTIONS, BUSINESS_CATEGORY_KEY_MAP, PERSONAL_CATEGORY_KEY_MAP } from '../utils/categoryDescriptions';
import logger from '../utils/logger';

const AssessmentModal = ({ assessmentId, allQuestions, answeredQuestions, onClose, onSave, initialQuestionId, assessmentType = 'business' }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const scrollContainerRef = useRef(null);
  const [showScoringHelp, setShowScoringHelp] = useState(false);
  const [isScoringGuideCollapsed, setIsScoringGuideCollapsed] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const questionRefs = useRef([]);
  const [expandedGuidance, setExpandedGuidance] = useState({});
  const [notes, setNotes] = useState({});
  const [expandedNotes, setExpandedNotes] = useState({});
  const [savingNote, setSavingNote] = useState({});
  const [noteSaved, setNoteSaved] = useState({});
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showCategoryHelp, setShowCategoryHelp] = useState(false);
  const hasJumpedRef = useRef(false);

  // Initialize answers and notes from already answered questions
  useEffect(() => {
    const initialAnswers = {};
    const initialNotes = {};
    answeredQuestions.forEach(q => {
      initialAnswers[q.question_id] = q.answer_value;
      if (q.comments) {
        initialNotes[q.question_id] = q.comments;
      }
    });
    setAnswers(initialAnswers);
    setNotes(initialNotes);
    setInitialLoadComplete(true);
  }, [answeredQuestions]);

  // Jump to initial question if specified — only fires ONCE per modal open.
  // hasJumpedRef prevents re-firing when the parent re-renders (which creates a
  // new allQuestions array reference even when the content hasn't changed).
  useEffect(() => {
    if (!hasJumpedRef.current && initialQuestionId && initialLoadComplete && allQuestions.length > 0) {
      hasJumpedRef.current = true;
      // Find the question's category and position
      const questionIndex = allQuestions.findIndex(q => q.question_id === initialQuestionId);
      if (questionIndex !== -1) {
        const question = allQuestions[questionIndex];

        // Find which category page this question is on
        const organized = {};
        allQuestions.forEach(q => {
          if (!organized[q.category]) {
            organized[q.category] = [];
          }
          organized[q.category].push(q);
        });

        const categoryKeys = Object.keys(organized);
        const categoryIndex = categoryKeys.indexOf(question.category);

        if (categoryIndex !== -1) {
          setCurrentPage(categoryIndex);

          // Find question index within that category
          const questionIndexInCategory = organized[question.category].findIndex(
            q => q.question_id === initialQuestionId
          );
          if (questionIndexInCategory !== -1) {
            // Small delay to ensure DOM is ready after page change
            setTimeout(() => {
              setCurrentQuestionIndex(questionIndexInCategory);
            }, 10);
          }
        }
      }
    }
  }, [initialQuestionId, initialLoadComplete, allQuestions]);

  // Reset question refs and scroll to top when changing pages (categories)
  useEffect(() => {
    questionRefs.current = [];
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentPage]);

  // Scroll to the current question — uses a delay so refs are populated
  // after the DOM updates from the page change.
  useEffect(() => {
    const timer = setTimeout(() => {
      questionRefs.current[currentQuestionIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [currentQuestionIndex, currentPage]);

  // Show loading state if no questions loaded
  if (!allQuestions || allQuestions.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Loading Questions...</h3>
          <p className="text-gray-600">Please wait while we load the assessment questions.</p>
        </div>
      </div>
    );
  }

  // Organize questions by category
  const organizeQuestions = () => {
    const organized = {};

    allQuestions.forEach(q => {
      if (!organized[q.category]) {
        organized[q.category] = {
          name: q.category_display,
          key: q.category,
          questions: []
        };
      }

      organized[q.category].questions.push(q);
    });

    return organized;
  };

  const questionsByCategory = organizeQuestions();
  const categoryKeys = Object.keys(questionsByCategory);
  const totalCategories = categoryKeys.length;

  // Get current category
  const currentCategoryKey = categoryKeys[currentPage];
  const currentCategory = currentCategoryKey ? questionsByCategory[currentCategoryKey] : null;

  // Calculate progress
  const totalQuestions = allQuestions.length;
  const totalAnswered = Object.keys(answers).length;
  const progressPercent = (totalAnswered / totalQuestions) * 100;

  // Calculate category progress
  const getCategoryAnsweredCount = (categoryKey) => {
    const category = questionsByCategory[categoryKey];
    return category.questions.filter(q => answers[q.question_id] !== undefined).length;
  };

  const currentCategoryAnswered = currentCategory 
    ? getCategoryAnsweredCount(currentCategoryKey) 
    : 0;
  const currentCategoryTotal = currentCategory ? currentCategory.questions.length : 0;
  const currentCategoryComplete = currentCategoryAnswered === currentCategoryTotal;

  const handleAnswerChange = async (question, value, questionIndex) => {
    // Update local state immediately
    setAnswers(prev => ({
      ...prev,
      [question.question_id]: value
    }));

    // Immediately advance to next question (optimistic UI)
    const nextQuestionIndex = questionIndex + 1;
    if (nextQuestionIndex < currentCategory.questions.length) {
      // Move to next question within same category - useEffect will handle scroll
      setCurrentQuestionIndex(nextQuestionIndex);
    } else if (currentPage < totalCategories - 1) {
      // Auto-advance to next category if this was the last question
      setTimeout(() => {
        setCurrentPage(currentPage + 1);
        setCurrentQuestionIndex(0);
      }, 50);
    }

    // Save to backend in background (don't wait)
    try {
      const responseData = {
        assessment_id: assessmentId,
        question_id: question.question_id,
        category: question.category,
        subject: question.subject,
        question_text: question.question_text,
        answer_value: value,
        answer_text: getAnswerText(value, question.scale_type),
        comments: notes[question.question_id] || '',
        rule_of_thumb: question.rule_of_thumb,
        considerations: question.considerations,
        related_task: question.related_task
      };

      // Use the correct save function based on assessment type (background save)
      const savePromise = assessmentType === 'personal'
        ? savePersonalAssessmentResponse(responseData)
        : saveAssessmentResponse(responseData);

      // Handle save result without blocking UI
      savePromise.then(result => {
        if (!result.success) {
          logger.error('Failed to save assessment answer', { error: result.error, questionId: question.question_id, assessmentId });
          // Optionally show a non-blocking error notification
          console.error('Failed to save response:', result.error);
        }
      }).catch(error => {
        logger.error('Error saving assessment answer', { error, questionId: question.question_id, assessmentId });
        console.error('Error saving response:', error);
      });
    } catch (error) {
      logger.error('Error initiating assessment save', { error, questionId: question.question_id, assessmentId });
    }
  };

  const handleNoteSave = async (question) => {
    const questionId = question.question_id;
    const noteText = notes[questionId] || '';

    // Set saving state
    setSavingNote(prev => ({ ...prev, [questionId]: true }));

    try {
      // Parse existing comments to get note history
      let noteHistory = [];
      const existingAnswer = answeredQuestions.find(q => q.question_id === questionId);
      if (existingAnswer && existingAnswer.comments) {
        try {
          noteHistory = JSON.parse(existingAnswer.comments);
          if (!Array.isArray(noteHistory)) {
            // Convert old format to new format
            noteHistory = [{ text: existingAnswer.comments, timestamp: new Date().toISOString() }];
          }
        } catch {
          // If not JSON, treat as old format
          noteHistory = existingAnswer.comments ? [{ text: existingAnswer.comments, timestamp: new Date().toISOString() }] : [];
        }
      }

      // Add new note to history if there's text
      if (noteText.trim()) {
        noteHistory.push({
          text: noteText,
          timestamp: new Date().toISOString()
        });
      }

      const responseData = {
        assessment_id: assessmentId,
        question_id: question.question_id,
        category: question.category,
        subject: question.subject,
        question_text: question.question_text,
        answer_value: answers[questionId] !== undefined ? answers[questionId] : null,
        answer_text: answers[questionId] !== undefined ? getAnswerText(answers[questionId], question.scale_type) : '',
        comments: JSON.stringify(noteHistory),
        rule_of_thumb: question.rule_of_thumb,
        considerations: question.considerations,
        related_task: question.related_task
      };

      // Use the correct save function based on assessment type
      const result = assessmentType === 'personal'
        ? await savePersonalAssessmentResponse(responseData)
        : await saveAssessmentResponse(responseData);

      if (!result.success) {
        logger.error('Failed to save assessment note', { error: result.error, questionId, assessmentId });
        alert('Failed to save note: ' + result.error);
      } else {
        // Clear the note textarea
        setNotes(prev => ({ ...prev, [questionId]: '' }));

        // Show success animation
        setNoteSaved(prev => ({ ...prev, [questionId]: true }));

        // Hide success animation after 2 seconds
        setTimeout(() => {
          setNoteSaved(prev => ({ ...prev, [questionId]: false }));
        }, 2000);

        // Trigger onSave callback to refresh data
        if (onSave) {
          await onSave();
        }
      }
    } catch (error) {
      logger.error('Error saving assessment note', { error, questionId, assessmentId });
      alert('Error saving note. Please try again.');
    } finally {
      setSavingNote(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const getAnswerText = (value, scaleType = 'comparative') => {
    if (value === 0) return 'N/A - Not Applicable';

    if (scaleType === 'documentation') {
      const docLabels = {
        1: "I Don't Know What You're Talking About",
        2: "I've Heard About It, But It's Not Clear",
        3: "It's Clear, But Not Documented",
        4: "It's Clear & Documented, But Not Transferable",
        5: "It's Clear, Documented, & Transferable",
        6: "It's Perfect - No Improvement Possible"
      };
      return docLabels[value] || '';
    }

    // Comparative questions (default)
    const comparativeLabels = {
      1: 'Weaker Than All Others',
      2: 'Weaker Than Most',
      3: 'Weak, But Gaining Strength',
      4: 'Strong, And Getting Stronger',
      5: 'Stronger Than Most',
      6: 'Stronger Than All Others'
    };
    return comparativeLabels[value] || '';
  };

  const getButtonColor = (value, isSelected) => {
    if (isSelected) {
      const selectedColors = {
        0: 'bg-gray-600 border-gray-800 text-white shadow-lg',
        1: 'bg-red-700 border-red-900 text-white shadow-lg',
        2: 'bg-orange-600 border-orange-800 text-white shadow-lg',
        3: 'bg-yellow-600 border-yellow-800 text-white shadow-lg',
        4: 'bg-blue-700 border-blue-900 text-white shadow-lg',
        5: 'bg-green-700 border-green-900 text-white shadow-lg',
        6: 'border-2 text-white shadow-lg'
      };

      // Custom gold color for score 6
      if (value === 6) {
        return selectedColors[6] + ' ' + 'bg-[#a07d5a] border-[#8b6f47]';
      }

      return selectedColors[value] || selectedColors[0];
    }

    const unselectedColors = {
      0: 'bg-gray-100 border-gray-400 text-gray-800 hover:border-gray-500 hover:bg-gray-200',
      1: 'bg-red-100 border-red-400 text-red-800 hover:border-red-500 hover:bg-red-200',
      2: 'bg-orange-100 border-orange-400 text-orange-800 hover:border-orange-500 hover:bg-orange-200',
      3: 'bg-yellow-100 border-yellow-400 text-yellow-800 hover:border-yellow-500 hover:bg-yellow-200',
      4: 'bg-blue-100 border-blue-400 text-blue-800 hover:border-blue-500 hover:bg-blue-200',
      5: 'bg-green-100 border-green-400 text-green-800 hover:border-green-500 hover:bg-green-200',
      6: 'border-2 hover:border-2'
    };

    // Custom gold color for score 6 (unselected)
    if (value === 6) {
      return unselectedColors[6] + ' ' + 'bg-[#e8d4bb] border-[#a07d5a] text-[#6b5333] hover:bg-[#d4c1a3] hover:border-[#8b6f47]';
    }

    return unselectedColors[value] || unselectedColors[0];
  };

  const getAnswerBadgeColor = (value) => {
    const colors = {
      0: 'bg-gray-100 text-gray-800 border-gray-300',
      1: 'bg-red-100 text-red-800 border-red-300',
      2: 'bg-orange-100 text-orange-800 border-orange-300',
      3: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      4: 'bg-blue-100 text-blue-800 border-blue-300',
      5: 'bg-green-100 text-green-800 border-green-300',
      6: 'border'
    };

    // Custom gold color for score 6 badge
    if (value === 6) {
      return colors[6] + ' ' + 'bg-[#f5e6d3] text-[#8b6f47] border-[#c49e73]';
    }

    return colors[value] || colors[0];
  };

  const handleNext = () => {
    if (currentPage < totalCategories - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleFinish = async () => {
    // Trigger reload to refresh scores
    if (onSave) {
      await onSave();
    }
    onClose();
  };

  if (!currentCategory) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl w-full max-w-full sm:max-w-3xl md:max-w-4xl lg:max-w-6xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* Header - Responsive */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex-1 min-w-0 pr-2">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
              {assessmentType === 'personal' ? 'Personal Gap Assessment' : 'Business Gap Assessment'}
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
              Category {currentPage + 1} of {totalCategories}: {currentCategory.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close"
          >
            <X size={24} className="sm:w-7 sm:h-7" />
          </button>
        </div>

        {/* Overall Progress Bar - Responsive */}
        <div className="px-4 sm:px-6 py-3 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-blue-900">
              Overall Progress: {totalAnswered} / {totalQuestions} questions
            </span>
            <span className="text-xs font-semibold text-blue-900">
              {Math.round(progressPercent)}%
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>

        {/* Category Progress - Responsive */}
        <div className="px-4 sm:px-6 py-3 bg-green-50 border-b border-green-100">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-semibold text-green-900">
                {currentCategory.name}: {currentCategoryAnswered} / {currentCategoryTotal} questions
              </span>
              {/* Category Help Button */}
              <button
                onClick={() => setShowCategoryHelp(true)}
                className={`flex items-center space-x-1 transition p-1.5 rounded-lg ${
                  assessmentType === 'personal'
                    ? 'text-purple-600 hover:text-purple-800 hover:bg-purple-100'
                    : 'text-blue-600 hover:text-blue-800 hover:bg-blue-100'
                }`}
                title={`Learn more about ${currentCategory.name}`}
              >
                <HelpCircle size={18} />
                <span className="text-xs font-medium">About this category</span>
              </button>
            </div>
            {currentCategoryComplete && (
              <span className="flex items-center text-sm font-semibold text-green-700">
                <Check size={16} className="mr-1" />
                Complete
              </span>
            )}
          </div>
          <div className="w-full bg-green-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(currentCategoryAnswered / currentCategoryTotal) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-6">
          {/* Inline Scoring Guide - Collapsible */}
          <div className="mb-8 flex justify-center">
            <div className="w-full max-w-3xl" style={{ transform: 'scale(1.125)', transformOrigin: 'top center' }}>
              {/* Collapsible Scoring Guide Card */}
              <div className="bg-white rounded-lg border-2 border-gray-300 shadow-sm mb-12">
                {/* Collapsible Header - FIXED: Changed from button to div */}
                <div
                  onClick={() => setIsScoringGuideCollapsed(!isScoringGuideCollapsed)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition rounded-t-lg cursor-pointer"
                >
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-bold text-gray-900">
                      Assessment Scoring Guide
                    </h3>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowScoringHelp(true);
                      }}
                      className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition"
                      title="Learn more about Clear, Documented, & Transferable"
                    >
                      <HelpCircle size={18} />
                      <span className="text-xs font-medium">What does "Clear, Documented, & Transferable" mean?</span>
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <span className="text-sm">{isScoringGuideCollapsed ? 'Show' : 'Hide'}</span>
                    {isScoringGuideCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                  </div>
                </div>

                {/* Collapsible Content */}
                {!isScoringGuideCollapsed && (
                  <div className="px-4 pb-4">
                    <div className="flex justify-center">
                      <img
                        src="/scoring-guide.png"
                        alt="Assessment Scoring Guide"
                        className="w-full h-auto rounded"
                        style={{ maxHeight: '220px', objectFit: 'contain' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Category Questions */}
          <div className="space-y-3">
            {currentCategory.questions.map((question, index) => {
              const currentAnswer = answers[question.question_id];
              const isAnswered = currentAnswer !== undefined;

              return (
                <div
                  key={question.question_id}
                  ref={(el) => (questionRefs.current[index] = el)}
                  className={`rounded-lg border transition-all duration-300 overflow-hidden ${
                    isAnswered
                      ? 'border-green-400 bg-white shadow-sm'
                      : currentQuestionIndex === index
                      ? 'border-blue-500 bg-white shadow-md ring-1 ring-blue-200'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Question Header - Compact & Modern */}
                  <div className={`p-4 ${
                    isAnswered
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                      : currentQuestionIndex === index
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600'
                      : 'bg-gradient-to-r from-gray-600 to-gray-700'
                  } text-white`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-white/70 uppercase tracking-wide">Subject</span>
                            {isAnswered && <Check size={14} className="text-white/90" />}
                          </div>
                          <p className="text-base font-semibold mb-2 leading-snug">
                            {question.question_title || question.subject}
                          </p>
                          <p className="text-sm text-white/90 leading-snug">
                            {question.question_text}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setExpandedNotes(prev => ({ ...prev, [question.question_id]: !prev[question.question_id] }))}
                        className="flex-shrink-0 p-1.5 bg-white/20 hover:bg-white/30 rounded-md transition-all"
                        title={expandedNotes[question.question_id] ? "Hide Note" : "Add Note"}
                      >
                        <FileText size={16} className={notes[question.question_id] ? 'text-yellow-200' : 'text-white'} />
                      </button>
                    </div>
                  </div>

                  {/* Answer Section - Compact */}
                  <div className="p-4 bg-gray-50">
                    <div className="grid grid-cols-7 gap-1.5">
                      {[0, 1, 2, 3, 4, 5, 6].map((value) => {
                        const isSelected = currentAnswer === value;
                        const shortLabels = {
                          0: 'N/A',
                          1: '1',
                          2: '2',
                          3: '3',
                          4: '4',
                          5: '5',
                          6: '6'
                        };

                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => handleAnswerChange(question, value, index)}
                            className={`py-2 px-1 rounded-md border-2 transition-all text-center ${
                              getButtonColor(value, isSelected)
                            } ${isSelected ? 'scale-105 shadow-md' : 'hover:scale-102'}`}
                            title={getAnswerText(value, question.scale_type)}
                          >
                            <div className="text-lg font-bold">{shortLabels[value]}</div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Answered Badge - Inline */}
                    {isAnswered && (
                      <div className="mt-2 flex items-center justify-center">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          getAnswerBadgeColor(currentAnswer)
                        }`}>
                          {getAnswerText(currentAnswer, question.scale_type)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Note Section - Compact */}
                  {expandedNotes[question.question_id] && (
                    <div className="px-4 py-3 bg-blue-50 border-t border-blue-100">
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                          📝 Personal Note
                        </label>
                        <textarea
                          value={notes[question.question_id] || ''}
                          onChange={(e) => setNotes(prev => ({ ...prev, [question.question_id]: e.target.value }))}
                          placeholder="Add your notes, thoughts, or action items..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          rows={3}
                        />
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => handleNoteSave(question)}
                            disabled={savingNote[question.question_id]}
                            className={`px-4 py-1.5 text-sm rounded-md font-medium transition-all flex items-center gap-1.5 ${
                              noteSaved[question.question_id]
                                ? 'bg-green-500 text-white'
                                : savingNote[question.question_id]
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {noteSaved[question.question_id] ? (
                              <>
                                <Check size={14} />
                                <span>Saved!</span>
                              </>
                            ) : savingNote[question.question_id] ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                <span>Saving...</span>
                              </>
                            ) : (
                              <span>Save Note</span>
                            )}
                          </button>
                          {noteSaved[question.question_id] && (
                            <span className="text-xs text-green-600 font-medium">
                              ✓ Saved
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Collapsible Guidance Sections - Compact */}
                  <div className="border-t border-gray-200">
                    {/* Rule of Thumb - Collapsible */}
                    {question.rule_of_thumb && (
                      <div className="border-b border-gray-100">
                        <button
                          onClick={() => setExpandedGuidance({
                            ...expandedGuidance,
                            [`${question.question_id}_rule`]: !expandedGuidance[`${question.question_id}_rule`]
                          })}
                          className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 transition text-left group"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm">💡</span>
                            <span className="font-medium text-gray-900 text-xs">Rule of Thumb</span>
                          </div>
                          {expandedGuidance[`${question.question_id}_rule`] ? (
                            <ChevronDown size={14} className="text-gray-400 group-hover:text-gray-600" />
                          ) : (
                            <ChevronRight size={14} className="text-gray-400 group-hover:text-gray-600" />
                          )}
                        </button>
                        {expandedGuidance[`${question.question_id}_rule`] && (
                          <div className="px-4 pb-3 pt-1 bg-blue-50/50">
                            <p className="text-xs text-gray-700 leading-relaxed">{question.rule_of_thumb}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Considerations - Collapsible */}
                    {question.considerations && (
                      <div>
                        <button
                          onClick={() => setExpandedGuidance({
                            ...expandedGuidance,
                            [`${question.question_id}_consider`]: !expandedGuidance[`${question.question_id}_consider`]
                          })}
                          className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 transition text-left group"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm">📋</span>
                            <span className="font-medium text-gray-900 text-xs">Why This Matters</span>
                          </div>
                          {expandedGuidance[`${question.question_id}_consider`] ? (
                            <ChevronDown size={14} className="text-gray-400 group-hover:text-gray-600" />
                          ) : (
                            <ChevronRight size={14} className="text-gray-400 group-hover:text-gray-600" />
                          )}
                        </button>
                        {expandedGuidance[`${question.question_id}_consider`] && (
                          <div className="px-4 pb-3 pt-1 bg-green-50/50">
                            <p className="text-xs text-gray-700 leading-relaxed">{question.considerations}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer with Navigation */}
        <div className="border-t border-gray-200 bg-gray-50">
          {/* Category Navigation Dots */}
          <div className="px-6 py-3 border-b border-gray-200">
            <div className="flex items-center justify-center space-x-2">
              {categoryKeys.map((catKey, index) => {
                const isComplete = getCategoryAnsweredCount(catKey) === questionsByCategory[catKey].questions.length;
                const isCurrent = index === currentPage;
                
                return (
                  <button
                    key={catKey}
                    onClick={() => setCurrentPage(index)}
                    className={`relative group transition-all ${
                      isCurrent 
                        ? 'w-12 h-3' 
                        : 'w-3 h-3'
                    }`}
                    title={questionsByCategory[catKey].name}
                  >
                    <div className={`w-full h-full rounded-full transition-all ${
                      isComplete 
                        ? 'bg-green-500' 
                        : isCurrent 
                        ? 'bg-blue-600' 
                        : 'bg-gray-300'
                    }`}>
                      {isComplete && (
                        <Check size={10} className="text-white absolute inset-0 m-auto" />
                      )}
                    </div>
                    {/* Tooltip */}
                    <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap pointer-events-none z-10">
                      {questionsByCategory[catKey].name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between p-6">
            <button
              onClick={handlePrevious}
              disabled={currentPage === 0}
              className={`flex items-center space-x-2 px-5 py-3 rounded-lg font-semibold transition ${
                currentPage === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <ChevronLeft size={20} />
              <span>Previous</span>
            </button>

            <div className="text-sm text-gray-600">
              Page {currentPage + 1} of {totalCategories}
            </div>

            {currentPage < totalCategories - 1 ? (
              <button
                onClick={handleNext}
                className="flex items-center space-x-2 px-5 py-3 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                <span>Next Category</span>
                <ChevronRight size={20} />
              </button>
            ) : (
              <button
                onClick={handleFinish}
                className={`px-6 py-3 rounded-lg font-semibold transition ${
                  totalAnswered === totalQuestions
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {totalAnswered === totalQuestions ? 'Finish & View Results' : 'Save & Close'}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Help Modal - Inside AssessmentModal for proper z-index - Responsive */}
      {showScoringHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-2 sm:p-4">
          <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl w-full max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Modal Header - Responsive */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 pr-2">
                Understanding "Clear, Documented, & Transferable"
              </h2>
              <button
                onClick={() => setShowScoringHelp(false)}
                className="text-gray-400 hover:text-gray-600 transition min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content - Responsive */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Introduction */}
              <div className={`border-l-4 p-4 rounded ${
                assessmentType === 'personal'
                  ? 'bg-purple-50 border-purple-500'
                  : 'bg-blue-50 border-blue-500'
              }`}>
                <p className="text-gray-700 leading-relaxed">
                  {assessmentType === 'personal' ? (
                    <>
                      These three attributes represent the <strong>gold standard</strong> for personal planning,
                      systems, and readiness that ensure smooth life transitions. Strong preparation in your personal
                      life protects your family and provides peace of mind during exit and beyond.
                    </>
                  ) : (
                    <>
                      These three attributes represent the <strong>gold standard</strong> for business processes,
                      systems, and operations that maximize transferable value. Buyers pay premium prices for
                      businesses that can run independently of the current owner.
                    </>
                  )}
                </p>
              </div>

              {/* Clear */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="bg-green-100 text-green-700 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                    1
                  </span>
                  Clear
                </h3>
                <div className="ml-11 space-y-2">
                  <p className="text-gray-700">
                    <strong>Definition:</strong> {assessmentType === 'personal'
                      ? 'Plans, responsibilities, and intentions are unambiguous, easy to understand, and clearly communicated to family and advisors.'
                      : 'Processes and responsibilities are unambiguous, easy to understand, and consistently executed.'}
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Examples:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>Defined estate plan with explicit beneficiary designations</li>
                          <li>Clear financial goals and retirement vision</li>
                          <li>Explicit family roles and responsibilities</li>
                          <li>Well-articulated values and legacy intentions</li>
                        </>
                      ) : (
                        <>
                          <li>Step-by-step sales process that anyone can follow</li>
                          <li>Defined roles with explicit job descriptions</li>
                          <li>Straightforward pricing methodology</li>
                          <li>Transparent financial reporting with clear KPIs</li>
                        </>
                      )}
                    </ul>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-2">Red Flags (Not Clear):</p>
                    <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>"My family knows what I want" (but nothing is explicitly stated)</li>
                          <li>Vague intentions with no specific plans</li>
                          <li>Family members have different understandings of your wishes</li>
                        </>
                      ) : (
                        <>
                          <li>"We just figure it out as we go"</li>
                          <li>Tribal knowledge that only veterans understand</li>
                          <li>Inconsistent execution across team members</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Documented */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                    2
                  </span>
                  Documented
                </h3>
                <div className="ml-11 space-y-2">
                  <p className="text-gray-700">
                    <strong>Definition:</strong> {assessmentType === 'personal'
                      ? 'Written plans, legal documents, and instructions exist that can be referenced, updated, and used by family and advisors.'
                      : 'Written procedures, policies, and systems exist that can be referenced, updated, and used for training.'}
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Examples:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>Executed estate planning documents (wills, trusts, powers of attorney)</li>
                          <li>Written financial plan with investment policy statement</li>
                          <li>Healthcare directives and end-of-life wishes documented</li>
                          <li>Family governance documents or family constitution</li>
                          <li>Asset inventory with account details and access information</li>
                        </>
                      ) : (
                        <>
                          <li>Standard Operating Procedures (SOPs) for key processes</li>
                          <li>Employee handbook and training manuals</li>
                          <li>Written contracts and agreements with customers/vendors</li>
                          <li>Organizational charts and process flowcharts</li>
                          <li>Knowledge base or wiki for common issues</li>
                        </>
                      )}
                    </ul>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-2">Red Flags (Not Documented):</p>
                    <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>"It's all in my head" or "I'll tell my spouse later"</li>
                          <li>No updated will or estate documents</li>
                          <li>Verbal agreements with no legal backing</li>
                        </>
                      ) : (
                        <>
                          <li>"It's all in my head" or "I'll show the new owner"</li>
                          <li>No written procedures or manuals</li>
                          <li>Reliance on verbal instructions</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Transferable */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="bg-purple-100 text-purple-700 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                    3
                  </span>
                  Transferable
                </h3>
                <div className="ml-11 space-y-2">
                  <p className="text-gray-700">
                    <strong>Definition:</strong> {assessmentType === 'personal'
                      ? 'Responsibilities, knowledge, and relationships can be effectively handed off to family members, heirs, or advisors without significant disruption or loss of continuity.'
                      : 'The system can be effectively handed off to a new person without significant loss of efficiency or quality. The business doesn\'t depend on any single individual.'}
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Examples:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>Spouse or trusted family member can access accounts and understand finances</li>
                          <li>Advisory team knows your family and can continue serving them</li>
                          <li>Executors and trustees are prepared and informed</li>
                          <li>Children understand family values, wealth philosophy, and expectations</li>
                          <li>Important relationships are introduced to family members</li>
                        </>
                      ) : (
                        <>
                          <li>New employees can be onboarded using documentation</li>
                          <li>Customer relationships aren't tied to one person</li>
                          <li>Systems and software can be accessed by others</li>
                          <li>Vendor relationships are company-based, not personal</li>
                          <li>Management team can operate without owner</li>
                        </>
                      )}
                    </ul>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-2">Red Flags (Not Transferable):</p>
                    <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>You're the only one who knows where things are or how things work</li>
                          <li>Advisors don't know your family or long-term intentions</li>
                          <li>Passwords and account access known only to you</li>
                          <li>Family would struggle to manage affairs in your absence</li>
                        </>
                      ) : (
                        <>
                          <li>Owner is the only one who can perform critical functions</li>
                          <li>Key relationships would leave with owner</li>
                          <li>Passwords, accounts only accessible to owner</li>
                          <li>Long training period required for handoff</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Why It Matters */}
              <div className={`border-l-4 p-4 rounded ${
                assessmentType === 'personal'
                  ? 'bg-purple-50 border-purple-500'
                  : 'bg-yellow-50 border-yellow-500'
              }`}>
                <h4 className={`font-bold mb-2 ${
                  assessmentType === 'personal' ? 'text-purple-900' : 'text-yellow-900'
                }`}>
                  💡 {assessmentType === 'personal' ? 'Why This Matters for Your Family' : 'Why This Matters for Exit Value'}
                </h4>
                <p className={`text-sm leading-relaxed ${
                  assessmentType === 'personal' ? 'text-purple-800' : 'text-yellow-800'
                }`}>
                  {assessmentType === 'personal' ? (
                    <>
                      Clear, documented, and transferable personal planning <strong>protects your family</strong> and
                      provides peace of mind during life transitions. Families with strong preparation experience{' '}
                      <strong>significantly less stress and conflict</strong> during difficult times. Without proper
                      planning, loved ones often face unnecessary confusion, delays, and financial complications when
                      they're most vulnerable.
                    </>
                  ) : (
                    <>
                      Buyers pay <strong>2-4x more</strong> for businesses with clear, documented, and transferable
                      systems because they reduce risk and enable a smooth transition. A business that depends on
                      the owner's personal knowledge and relationships is worth significantly less than one that
                      operates like a well-oiled machine.
                    </>
                  )}
                </p>
              </div>

              {/* Scoring Guide */}
              <div className="border-t pt-4">
                <h4 className="font-bold text-gray-900 mb-3">Assessment Scoring Scale (0-6):</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center bg-[#c49e73] text-white">6</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Exemplary & Optimized</p>
                      <p className="text-xs text-gray-600">Fully clear, comprehensively documented, and seamlessly transferable. Best-in-class execution with no meaningful improvements needed.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">5</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Clear, Documented & Transferable</p>
                      <p className="text-xs text-gray-600">Meets all three core criteria with strong execution. Minor refinements possible but fundamentally sound and ready for transition.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">4</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Clear & Documented, Not Yet Transferable</p>
                      <p className="text-xs text-gray-600">
                        {assessmentType === 'personal'
                          ? 'Well-defined and properly documented, but remains dependent on you personally. Requires knowledge transfer planning to ensure family and advisors can act independently.'
                          : 'Well-defined and properly documented, but remains dependent on specific individuals. Requires knowledge transfer planning to reduce key person risk.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">3</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Clear But Lacking Documentation</p>
                      <p className="text-xs text-gray-600">
                        {assessmentType === 'personal'
                          ? 'You understand what needs to happen and have clear intentions, but nothing is formally written down. Important knowledge exists only in your head.'
                          : 'Processes are understood and consistently executed, but not formally written down. Institutional knowledge exists but isn\'t captured in accessible formats.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">2</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Conceptually Understood, Poorly Defined</p>
                      <p className="text-xs text-gray-600">You recognize the importance and generally understand the concept, but execution is inconsistent, unclear, or varies by person. Needs structure and standardization.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-red-100 text-red-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">1</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Unfamiliar or Absent</p>
                      <p className="text-xs text-gray-600">
                        {assessmentType === 'personal'
                          ? 'This area is not currently addressed in your personal planning, or you\'re unclear on what the question is asking. Represents a significant gap requiring immediate attention.'
                          : 'This area is not currently addressed in your business, or you\'re unclear on what the question is asking. Represents a significant gap requiring immediate attention.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">0</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Not Applicable (N/A)</p>
                      <p className="text-xs text-gray-600">This question does not apply to your business model or industry. These responses are excluded from your final score calculation.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowScoringHelp(false)}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Help Modal - Responsive */}
      {showCategoryHelp && currentCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-2 sm:p-4">
          <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl w-full max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Modal Header - Responsive */}
            <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${
              assessmentType === 'personal' ? 'border-purple-200 bg-purple-50' : 'border-blue-200 bg-blue-50'
            }`}>
              <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                <HelpCircle size={20} className={`${assessmentType === 'personal' ? 'text-purple-600' : 'text-blue-600'} sm:w-6 sm:h-6 flex-shrink-0`} />
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                  {currentCategory.name}
                </h2>
              </div>
              <button
                onClick={() => setShowCategoryHelp(false)}
                className="text-gray-400 hover:text-gray-600 transition min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content - Responsive */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {assessmentType === 'business' ? (
                // Business Assessment - Detailed format with summary, detailed, and keyQuestions
                (() => {
                  // Map category key to full description name
                  const fullCategoryName = BUSINESS_CATEGORY_KEY_MAP[currentCategoryKey] || currentCategory.name;
                  const categoryDesc = BUSINESS_CATEGORY_DESCRIPTIONS[fullCategoryName];
                  if (!categoryDesc) {
                    return (
                      <div className="text-gray-700">
                        <p>Category information not available.</p>
                      </div>
                    );
                  }

                  return (
                    <>
                      {/* Summary */}
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                        <h3 className="font-bold text-blue-900 mb-2">Summary</h3>
                        <p className="text-gray-700 leading-relaxed">
                          {categoryDesc.summary}
                        </p>
                      </div>

                      {/* Detailed Explanation */}
                      <div>
                        <h3 className="font-bold text-gray-900 mb-3 text-lg">What This Category Measures</h3>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                          {categoryDesc.detailed}
                        </p>
                      </div>

                      {/* Key Questions */}
                      {categoryDesc.keyQuestions && categoryDesc.keyQuestions.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="font-bold text-gray-900 mb-3">Key Questions to Consider</h3>
                          <ul className="space-y-2">
                            {categoryDesc.keyQuestions.map((question, idx) => (
                              <li key={idx} className="flex items-start space-x-2">
                                <span className="text-blue-600 font-bold mt-0.5">•</span>
                                <span className="text-gray-700">{question}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  );
                })()
              ) : (
                // Personal Assessment - Detailed format with summary, detailed, and keyQuestions
                (() => {
                  // Map category key to full description name
                  const fullCategoryName = PERSONAL_CATEGORY_KEY_MAP[currentCategoryKey] || currentCategory.name;
                  const categoryDesc = PERSONAL_CATEGORY_DESCRIPTIONS[fullCategoryName];
                  if (!categoryDesc) {
                    return (
                      <div className="text-gray-700">
                        <p>Category information not available.</p>
                      </div>
                    );
                  }

                  return (
                    <>
                      {/* Summary */}
                      <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                        <h3 className="font-bold text-purple-900 mb-2">Summary</h3>
                        <p className="text-gray-700 leading-relaxed">
                          {categoryDesc.summary}
                        </p>
                      </div>

                      {/* Detailed Explanation */}
                      <div>
                        <h3 className="font-bold text-gray-900 mb-3 text-lg">What This Category Measures</h3>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                          {categoryDesc.detailed}
                        </p>
                      </div>

                      {/* Key Questions */}
                      {categoryDesc.keyQuestions && categoryDesc.keyQuestions.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="font-bold text-gray-900 mb-3">Key Questions to Consider</h3>
                          <ul className="space-y-2">
                            {categoryDesc.keyQuestions.map((question, idx) => (
                              <li key={idx} className="flex items-start space-x-2">
                                <span className="text-purple-600 font-bold mt-0.5">•</span>
                                <span className="text-gray-700">{question}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  );
                })()
              )}

              {/* Why This Matters */}
              <div className={`border-l-4 p-4 rounded ${
                assessmentType === 'personal'
                  ? 'bg-purple-50 border-purple-500'
                  : 'bg-blue-50 border-blue-500'
              }`}>
                <h4 className={`font-bold mb-2 ${
                  assessmentType === 'personal' ? 'text-purple-900' : 'text-blue-900'
                }`}>
                  💡 Why This Matters
                </h4>
                <p className={`text-sm leading-relaxed ${
                  assessmentType === 'personal' ? 'text-purple-800' : 'text-blue-800'
                }`}>
                  {assessmentType === 'personal'
                    ? 'This category helps you evaluate your personal readiness for life after exit. A successful transition requires preparation in all areas of life, not just business.'
                    : 'This category is a critical factor in determining your business value and attractiveness to buyers. Strong performance here increases your valuation and improves your negotiating position.'
                  }
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`p-6 border-t border-gray-200 ${
              assessmentType === 'personal' ? 'bg-purple-50' : 'bg-blue-50'
            }`}>
              <button
                onClick={() => setShowCategoryHelp(false)}
                className={`w-full px-6 py-3 rounded-lg font-semibold transition ${
                  assessmentType === 'personal'
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

AssessmentModal.propTypes = {
  assessmentId: PropTypes.number.isRequired,
  allQuestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      question_text: PropTypes.string.isRequired,
      category: PropTypes.string.isRequired,
      rule_of_thumb: PropTypes.string,
      guidance: PropTypes.string,
      question_order: PropTypes.number
    })
  ).isRequired,
  answeredQuestions: PropTypes.arrayOf(
    PropTypes.shape({
      question_id: PropTypes.number.isRequired,
      answer_value: PropTypes.number,
      comments: PropTypes.string
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialQuestionId: PropTypes.number,
  assessmentType: PropTypes.oneOf(['business', 'personal'])
};

AssessmentModal.defaultProps = {
  initialQuestionId: null,
  assessmentType: 'business'
};

export default AssessmentModal;
